import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  AdminLogin,
  AdminDashboard,
  CompanyManagement,
  CompanyDetailView,
  CompanyAddPage,
  CompanyEditPage,
  CompanyImportPage,
  AnalyticsDashboard,
  UserManagement,
  UserAddPage,
  ClaimsManagement,
  RequestsPage,
  ChangeRequestsPage,
  UnifiedRequestsPage,
  ServiceTypesPage,
  AdminSettingsPage,
  ProvincesPage,
  FeaturedCompaniesPage,
  CompanyVerificationPage,
  ActivitiesPage,
  FeedbackManagement,
  NotificationsManagement,
  CompanyAnalytics,
  UserAnalytics,
  SearchAnalytics,
  ContactAnalytics
} from '../pages/admin';
import {
  AdminRouteGuard,
  UnauthorizedPage
} from '../components/admin';
import { AdminPermissions } from '../hooks/admin';

const AdminRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Admin Routes */}
      <Route path="/login" element={<AdminLogin />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Protected Admin Routes */}
      <Route
        path="/dashboard"
        element={
          <AdminRouteGuard requiredRole={['ADMIN', 'SUPER_ADMIN', 'MODERATOR']}>
            <AdminDashboard />
          </AdminRouteGuard>
        }
      />

      {/* Company Management Routes */}
      <Route
        path="/companies"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.COMPANY_VIEW}
          >
            <CompanyManagement />
          </AdminRouteGuard>
        }
      />
      <Route
        path="/companies/add"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.COMPANY_CREATE}
          >
            <CompanyAddPage />
          </AdminRouteGuard>
        }
      />
      <Route
        path="/companies/view/:id"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.COMPANY_VIEW}
          >
            <CompanyDetailView />
          </AdminRouteGuard>
        }
      />
      <Route
        path="/companies/edit/:id"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.COMPANY_UPDATE}
          >
            <CompanyEditPage />
          </AdminRouteGuard>
        }
      />
      <Route
        path="/companies/import"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.COMPANY_CREATE}
          >
            <CompanyImportPage />
          </AdminRouteGuard>
        }
      />

      {/* User Management Routes */}
      <Route
        path="/users"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.USER_VIEW}
          >
            <UserManagement />
          </AdminRouteGuard>
        }
      />
      <Route
        path="/users/add"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.USER_CREATE}
          >
            <UserAddPage />
          </AdminRouteGuard>
        }
      />

      {/* Analytics Routes */}
      <Route
        path="/analytics"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN', 'MODERATOR']}
            requiredPermission={AdminPermissions.ANALYTICS_VIEW}
          >
            <AnalyticsDashboard />
          </AdminRouteGuard>
        }
      />
      <Route
        path="/analytics/companies"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN', 'MODERATOR']}
            requiredPermission={AdminPermissions.ANALYTICS_VIEW}
          >
            <CompanyAnalytics />
          </AdminRouteGuard>
        }
      />
      <Route
        path="/analytics/users"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN', 'MODERATOR']}
            requiredPermission={AdminPermissions.ANALYTICS_VIEW}
          >
            <UserAnalytics />
          </AdminRouteGuard>
        }
      />
      <Route
        path="/analytics/search"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN', 'MODERATOR']}
            requiredPermission={AdminPermissions.ANALYTICS_VIEW}
          >
            <SearchAnalytics />
          </AdminRouteGuard>
        }
      />
      <Route
        path="/analytics/contacts"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN', 'MODERATOR']}
            requiredPermission={AdminPermissions.ANALYTICS_VIEW}
          >
            <ContactAnalytics />
          </AdminRouteGuard>
        }
      />

      {/* Claims Management Routes */}
      <Route
        path="/claims"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.COMPANY_APPROVE}
          >
            <ClaimsManagement />
          </AdminRouteGuard>
        }
      />

      {/* Requests Management Routes (New UI with Tabs & Modals) */}
      <Route
        path="/requests"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.COMPANY_APPROVE}
          >
            <RequestsPage />
          </AdminRouteGuard>
        }
      />

      {/* Change Requests Routes (Create/Edit Company Requests) */}
      <Route
        path="/change-requests"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.COMPANY_APPROVE}
          >
            <ChangeRequestsPage />
          </AdminRouteGuard>
        }
      />

      {/* Unified Requests Page (All 3 types: CREATE, CLAIM, UPDATE) */}
      <Route
        path="/all-requests"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.COMPANY_APPROVE}
          >
            <UnifiedRequestsPage />
          </AdminRouteGuard>
        }
      />

      {/* Service Types Management Routes */}
      <Route
        path="/service-types"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.COMPANY_UPDATE}
          >
            <ServiceTypesPage />
          </AdminRouteGuard>
        }
      />

      {/* Provinces Management Routes */}
      <Route
        path="/provinces"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.COMPANY_UPDATE}
          >
            <ProvincesPage />
          </AdminRouteGuard>
        }
      />

      {/* Featured Companies Management Routes */}
      <Route
        path="/featured-companies"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.COMPANY_UPDATE}
          >
            <FeaturedCompaniesPage />
          </AdminRouteGuard>
        }
      />

      {/* Company Verification Routes */}
      <Route
        path="/company-verification"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
            requiredPermission={AdminPermissions.COMPANY_APPROVE}
          >
            <CompanyVerificationPage />
          </AdminRouteGuard>
        }
      />

      {/* Settings Page (Master Data Hub) */}
      <Route
        path="/settings"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
          >
            <AdminSettingsPage />
          </AdminRouteGuard>
        }
      />

      {/* Activities Page */}
      <Route
        path="/activities"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN', 'MODERATOR']}
          >
            <ActivitiesPage />
          </AdminRouteGuard>
        }
      />

      {/* Feedback Management Routes */}
      <Route
        path="/feedback"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN', 'MODERATOR']}
          >
            <FeedbackManagement />
          </AdminRouteGuard>
        }
      />

      {/* Notifications Management Routes */}
      <Route
        path="/notifications"
        element={
          <AdminRouteGuard
            requiredRole={['ADMIN', 'SUPER_ADMIN']}
          >
            <NotificationsManagement />
          </AdminRouteGuard>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
    </Routes>
  );
};

export default AdminRoutes;